import {
  PlayerPublic,
  postOrganizationsByOrgIdPlayersByPlayerExternalIdEdit,
  postOrganizationsByOrgIdPlayersByPlayerIdDeleteMutation,
  SideType,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Icons,
  isPopulatedArray,
  toast,
  Tooltip,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import PlayerEditModal from "@/components/PlayerEditModal";
import { useCurrentOrganization } from "@/hooks/use-app-context";
import { removeOrgPrefix } from "@/lib/utils";
import { capitalize } from "@/utils/capitalize";
import { isOfficialPlayer } from "@/utils/isOfficialPlayer";
import { splitName } from "@/utils/splitName";

const Actions = ({
  row,
  showJerseyNumber: showJerseyNumberProp = false,
}: {
  row: PlayerPublic & { isVerifiedSource?: boolean };
  showJerseyNumber?: boolean;
}) => {
  const { orgId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  const deletePlayer = useMutation({
    ...postOrganizationsByOrgIdPlayersByPlayerIdDeleteMutation(),
    onError: () => {
      toast("You don't have permissions to delete this user", {
        variant: "error",
      });
    },
  });

  const confirm = useConfirmStore((state) => state.confirm);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Confirm Player Deletion",
      description: "Are you sure you want to remove this player?",
      confirmText: "Delete Player",
      confirmButtonVariant: "destructive",
    });

    if (confirmed) {
      useConfirmStore.setState({ isPending: true });

      await deletePlayer.mutateAsync(
        {
          path: {
            org_id: encodeURIComponent(orgId!) ?? "",
            player_id: encodeURIComponent(row.external_ids[0]),
          },
        },
        {
          onSettled: () => {
            useConfirmStore.setState({ isOpen: false, isPending: false });
          },
          onSuccess: async () => {
            toast("Player removed", {
              variant: "success",
            });

            await new Promise((resolve) => setTimeout(resolve, 1000));

            queryClient.invalidateQueries({
              queryKey: ["getOrganizationsByOrgIdPlayers", orgId],
            });
          },
        },
      );
    }
  };

  const updateRowAttributes = useCallback(
    async (values: { external_ids?: string[] } & Record<string, unknown>) => {
      await postOrganizationsByOrgIdPlayersByPlayerExternalIdEdit({
        path: {
          org_id: removeOrgPrefix(orgId!) ?? "",
          player_external_id: encodeURIComponent(
            values.external_ids?.[0] ?? "",
          ),
        },
        body: {
          firstName: values.firstName as string,
          lastName: values.lastName as string,
          bats: values.hit_side as SideType,
          throws: values.pitch_side as SideType,
        },
      });
    },
    [orgId],
  );

  const [isPlayerEditModalOpen, setIsPlayerEditModalOpen] = useState(false);
  return (
    <div className="flex space-x-2">
      <Tooltip
        isDisabled={!row.isVerifiedSource}
        content="Editing is disabled because this player is from a verified source."
      >
        <Button
          size="icon"
          isDisabled={row.isVerifiedSource}
          variant="ghost"
          onClick={() => setIsPlayerEditModalOpen(true)}
          className="text-blue"
        >
          <Icons.PencilOutline />
        </Button>
      </Tooltip>

      <PlayerEditModal
        open={isPlayerEditModalOpen}
        onOpenChange={setIsPlayerEditModalOpen}
        defaultValues={{
          firstName: row.known_names?.[0]?.split(",")[1] ?? "",
          lastName: row.known_names?.[0]?.split(",")[0] ?? "",
          hit_side: row.hit_side as SideType,
          pitch_side: row.pitch_side as "left" | "right",
          jerseyNumber: showJerseyNumberProp
            ? ((row as unknown as { jerseyNumber?: number | string })
                .jerseyNumber ?? "")
            : undefined,
        }}
        onSubmit={async (newValues) => {
          await updateRowAttributes({ ...row, ...newValues });
        }}
        showJerseyNumber={showJerseyNumberProp}
      />

      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleDelete()}
        className="text-blue"
      >
        <Icons.Trash />
      </Button>
    </div>
  );
};

export const usePlayerColumns = ({
  shouldRenderActions,
  disableSorting = true,
  showJerseyNumber = false,
}: {
  shouldRenderActions: boolean;
  disableSorting?: boolean;
  showJerseyNumber?: boolean;
}) => {
  const actionsColumn: ColumnDef<PlayerPublic> = useMemo(
    () => ({
      header: "",
      enableSorting: !disableSorting,
      id: "actions",
      cell: ({ row: { original: row } }) => {
        return (
          <Actions
            row={{ ...row, isVerifiedSource: isOfficialPlayer(row) }}
            showJerseyNumber={showJerseyNumber}
          />
        );
      },
    }),
    [disableSorting, showJerseyNumber],
  );

  const playersColumns = useMemo((): ColumnDef<PlayerPublic>[] => {
    const columns: ColumnDef<PlayerPublic>[] = [
      showJerseyNumber
        ? {
            accessorKey: "jerseyNumber",
            header: "JERSEY #",
            size: 80,
            enableSorting: !disableSorting,
            cell: ({ row: { original: row } }) => {
              return (
                (row as unknown as { jerseyNumber?: number | string })
                  .jerseyNumber ?? "-"
              );
            },
          }
        : {
            accessorKey: "id",
            header: "ID",
            size: 80,
            enableSorting: !disableSorting,
            cell: ({ row: { original: row } }) => {
              return isPopulatedArray(row.external_ids)
                ? row.external_ids.join(", ")
                : "-";
            },
          },
      {
        header: "First Name",
        enableSorting: !disableSorting,
        accessorKey: "firstName",
        size: 125,
        cell: ({ row: { original: row } }) => {
          const { firstName } = splitName(row.known_names?.[0] ?? "");
          const playerId = row.external_ids?.[0];

          return playerId ? (
            <Link
              to={`/players/${playerId}/home`}
              className="text-blue hover:underline"
            >
              {firstName}
            </Link>
          ) : (
            firstName
          );
        },
      },
      {
        header: "Last Name",
        size: 125,
        enableSorting: !disableSorting,
        accessorKey: "lastName",
        cell: ({ row: { original: row } }) => {
          const { lastName } = splitName(row.known_names?.[0] ?? "");

          return lastName;
        },
      },
      {
        accessorKey: "pitch_side",
        enableSorting: !disableSorting,
        header: "Throws",
        size: 100,
        cell: ({ row: { original: row } }) => {
          return row.pitch_side ? capitalize(row.pitch_side) : "";
        },
      },
      {
        accessorKey: "hit_side",
        size: 100,
        enableSorting: !disableSorting,
        header: "Bats",
        cell: ({ row: { original: row } }) => {
          return row.hit_side ? capitalize(row.hit_side) : "";
        },
      },
    ];

    if (shouldRenderActions) {
      columns.push(actionsColumn);
    }

    return columns;
  }, [actionsColumn, disableSorting, shouldRenderActions, showJerseyNumber]);

  return { playersColumns };
};
